import * as React from "react";

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
}

export interface UseWizardOptions {
  steps: WizardStep[];
  initialStepId?: string;
  canNavigate?: (fromId: string, toId: string) => Promise<boolean> | boolean;
}

export function useWizard({ steps, initialStepId, canNavigate }: UseWizardOptions) {
  if (steps.length === 0) {
    throw new Error("useWizard: steps array cannot be empty");
  }

  const [currentStepId, setCurrentStepId] = React.useState<string>(initialStepId || steps[0].id);

  const stepIndex = steps.findIndex((s) => s.id === currentStepId);
  const hasPrev = stepIndex > 0;
  const hasNext = stepIndex < steps.length - 1;

  const goTo = async (id: string) => {
    if (id === currentStepId) return true;
    if (canNavigate) {
      const ok = await canNavigate(currentStepId, id);
      if (!ok) return false;
    }
    setCurrentStepId(id);
    return true;
  };

  const next = async () => {
    if (!hasNext || stepIndex === -1) return false;
    return goTo(steps[stepIndex + 1].id);
  };

  const prev = async () => {
    if (!hasPrev || stepIndex === -1) return false;
    return goTo(steps[stepIndex - 1].id);
  };

  return {
    steps,
    currentStepId,
    setCurrentStepId: goTo,
    next,
    prev,
    hasNext,
    hasPrev,
    stepIndex,
  };
}
